import { useState, useEffect } from 'react';
import axios from 'axios';
import { MessageSquare, Plus, Search, Send, X, User, Building, Clock, Megaphone, Bell } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Communications() {
  const { user } = useAuth();
  const [communications, setCommunications] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('messages');
  const [filterStatus, setFilterStatus] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [selectedComm, setSelectedComm] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [formData, setFormData] = useState({
    entity_id: '', unit_id: '', subject: '', category: 'general', priority: 'medium', message: ''
  });
  const [announcementData, setAnnouncementData] = useState({
    entity_id: '', title: '', content: '', priority: 'normal'
  });

  const isBoard = user?.role === 'board';
  const isResident = user?.role === 'resident';
  const canCreateAnnouncements = ['admin', 'manager', 'employee'].includes(user?.role);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [commsRes, propsRes, announcementsRes] = await Promise.all([
        axios.get(`${API}/communications`),
        axios.get(`${API}/corporations`),
        axios.get(`${API}/announcements`).catch(() => ({ data: [] }))
      ]);
      setCommunications(commsRes.data);
      setProperties(propsRes.data);
      setAnnouncements(announcementsRes.data || []);
    } catch (error) { console.error('Failed to fetch data:', error); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/communications`, formData);
      setShowNewModal(false);
      setFormData({ entity_id: '', unit_id: '', subject: '', category: 'general', priority: 'medium', message: '' });
      fetchData();
    } catch (error) { console.error('Failed to create message:', error); }
  };

  const handleAnnouncementSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/announcements`, announcementData);
      setShowAnnouncementModal(false);
      setAnnouncementData({ entity_id: '', title: '', content: '', priority: 'normal' });
      fetchData();
    } catch (error) { console.error('Failed to create announcement:', error); }
  };

  const sendReply = async () => {
    if (!replyMessage.trim() || !selectedComm) return;
    try {
      await axios.post(`${API}/communications/${selectedComm.id}/reply`, { message: replyMessage });
      setReplyMessage('');
      fetchData();
      const updated = communications.find(c => c.id === selectedComm.id);
      if (updated) setSelectedComm(updated);
    } catch (error) { console.error('Failed to send reply:', error); }
  };

  const updateStatus = async (commId, newStatus) => {
    try {
      await axios.patch(`${API}/communications/${commId}`, { status: newStatus });
      setCommunications(communications.map(c => c.id === commId ? { ...c, status: newStatus } : c));
      if (selectedComm?.id === commId) {
        setSelectedComm({ ...selectedComm, status: newStatus });
      }
    } catch (error) { console.error('Failed to update status:', error); }
  };

  const filteredCommunications = communications.filter(c => !filterStatus || c.status === filterStatus);

  const getPriorityBadge = (priority) => {
    const styles = { high: 'bg-rose-100 text-rose-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-slate-100 text-slate-700', urgent: 'bg-rose-100 text-rose-700', normal: 'bg-blue-100 text-blue-700' };
    return styles[priority] || styles.medium;
  };

  if (loading) { return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>; }

  const openCount = communications.filter(c => c.status === 'open').length;

  return (
    <div className="space-y-6" data-testid="communications-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div><h1 className="text-2xl font-bold text-slate-900">Communications</h1><p className="text-slate-500">Messages and building announcements</p></div>
        <div className="flex gap-2">
          {canCreateAnnouncements && (
            <button onClick={() => setShowAnnouncementModal(true)} className="inline-flex items-center gap-2 px-4 py-2 border border-indigo-600 text-indigo-600 rounded-lg hover:bg-indigo-50" data-testid="new-announcement-btn">
              <Megaphone className="w-4 h-4" /> New Announcement
            </button>
          )}
          <button onClick={() => setShowNewModal(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-lg hover:from-indigo-700 hover:to-violet-700" data-testid="new-message-btn">
            <Plus className="w-4 h-4" /> New Message
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <div className="flex gap-4">
          <button onClick={() => setActiveTab('messages')} className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors ${activeTab === 'messages' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            <MessageSquare className="w-4 h-4 inline mr-2" /> Messages {openCount > 0 && <span className="ml-1 bg-indigo-100 text-indigo-700 text-xs px-2 py-0.5 rounded-full">{openCount}</span>}
          </button>
          <button onClick={() => setActiveTab('announcements')} className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors ${activeTab === 'announcements' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            <Megaphone className="w-4 h-4 inline mr-2" /> Building Announcements
          </button>
        </div>
      </div>

      {/* Messages Tab */}
      {activeTab === 'messages' && (
        <div className="space-y-4">
          {/* Status Filter */}
          <div className="flex gap-4 items-center">
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-4 py-2 border border-slate-300 rounded-lg bg-white">
              <option value="">All Messages</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
            </select>
            <span className="text-sm text-slate-500">
              Showing {filteredCommunications.length} of {communications.length} messages
            </span>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200"><h3 className="font-semibold text-slate-900">Conversations</h3></div>
            <div className="divide-y divide-slate-200 max-h-[600px] overflow-y-auto">
              {filteredCommunications.map((comm) => (
                <div key={comm.id} onClick={() => setSelectedComm(comm)} className={`p-4 cursor-pointer hover:bg-slate-50 ${selectedComm?.id === comm.id ? 'bg-indigo-50 border-l-4 border-indigo-600' : ''}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{comm.subject}</p>
                      <p className="text-xs text-slate-500 mt-1">{comm.created_by} • {comm.entity_name}</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      comm.status === 'open' ? 'bg-emerald-100 text-emerald-700' : 
                      comm.status === 'in_progress' ? 'bg-amber-100 text-amber-700' : 
                      'bg-slate-100 text-slate-600'
                    }`}>{comm.status === 'in_progress' ? 'In Progress' : comm.status}</span>
                  </div>
                </div>
              ))}
              {filteredCommunications.length === 0 && <div className="p-8 text-center text-slate-500">No messages found</div>}
            </div>
          </div>

          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 overflow-hidden">
            {selectedComm ? (
              <div className="h-full flex flex-col">
                <div className="p-4 border-b border-slate-200 bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-slate-900">{selectedComm.subject}</h3>
                      <p className="text-sm text-slate-500">{selectedComm.entity_name} {selectedComm.unit_number && `• Unit ${selectedComm.unit_number}`}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${getPriorityBadge(selectedComm.priority)}`}>{selectedComm.priority}</span>
                      <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700 capitalize">{selectedComm.category}</span>
                    </div>
                  </div>
                  {/* Status Update Buttons */}
                  {['admin', 'manager', 'employee'].includes(user?.role) && (
                    <div className="mt-3 flex gap-2">
                      <span className="text-xs text-slate-500 self-center mr-2">Set Status:</span>
                      <button onClick={() => updateStatus(selectedComm.id, 'open')} className={`text-xs px-3 py-1 rounded-full ${selectedComm.status === 'open' ? 'bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'}`}>Open</button>
                      <button onClick={() => updateStatus(selectedComm.id, 'in_progress')} className={`text-xs px-3 py-1 rounded-full ${selectedComm.status === 'in_progress' ? 'bg-amber-600 text-white' : 'bg-amber-100 text-amber-700 hover:bg-amber-200'}`}>In Progress</button>
                      <button onClick={() => updateStatus(selectedComm.id, 'resolved')} className={`text-xs px-3 py-1 rounded-full ${selectedComm.status === 'resolved' ? 'bg-slate-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>Resolved</button>
                    </div>
                  )}
                </div>
                <div className="flex-1 p-4 overflow-y-auto max-h-[400px] space-y-4">
                  {selectedComm.messages?.map((msg, idx) => (
                    <div key={idx} className={`flex ${msg.sender_role === 'resident' ? 'justify-start' : 'justify-end'}`}>
                      <div className={`max-w-[80%] p-3 rounded-xl ${msg.sender_role === 'resident' ? 'bg-slate-100' : 'bg-indigo-600 text-white'}`}>
                        <p className="text-sm">{msg.message}</p>
                        <p className={`text-xs mt-1 ${msg.sender_role === 'resident' ? 'text-slate-500' : 'text-indigo-200'}`}>{msg.sender} • {new Date(msg.timestamp).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="p-4 border-t border-slate-200 bg-slate-50">
                  <div className="flex gap-2">
                    <input type="text" value={replyMessage} onChange={(e) => setReplyMessage(e.target.value)} placeholder="Type your reply..." className="flex-1 px-4 py-2 border border-slate-300 rounded-lg" onKeyPress={(e) => e.key === 'Enter' && sendReply()} />
                    <button onClick={sendReply} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"><Send className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-slate-500 py-20">
                <div className="text-center">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Select a conversation to view</p>
                </div>
              </div>
            )}
          </div>
        </div>
        </div>
      )}

      {/* Announcements Tab */}
      {activeTab === 'announcements' && (
        <div className="space-y-4">
          {announcements.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <Megaphone className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500">No building announcements yet</p>
              {canCreateAnnouncements && <button onClick={() => setShowAnnouncementModal(true)} className="mt-4 text-indigo-600 hover:underline">Create the first announcement</button>}
            </div>
          ) : (
            announcements.map((announcement) => (
              <div key={announcement.id} className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-xl ${announcement.priority === 'urgent' ? 'bg-rose-100' : 'bg-indigo-100'}`}>
                    {announcement.priority === 'urgent' ? <Bell className="w-6 h-6 text-rose-600" /> : <Megaphone className="w-6 h-6 text-indigo-600" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-slate-900">{announcement.title}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getPriorityBadge(announcement.priority)}`}>{announcement.priority}</span>
                    </div>
                    <p className="text-sm text-slate-500 mb-3">{announcement.entity_name || 'All Properties'} • {new Date(announcement.created_at).toLocaleDateString()}</p>
                    <p className="text-slate-700">{announcement.content}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* New Message Modal */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">New Message</h3>
              <button onClick={() => setShowNewModal(false)} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Property</label>
                <select required value={formData.entity_id} onChange={(e) => setFormData({...formData, entity_id: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg">
                  <option value="">Select property</option>
                  {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Subject</label>
                <input type="text" required value={formData.subject} onChange={(e) => setFormData({...formData, subject: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                  <select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg">
                    <option value="general">General Inquiry</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="payment">Payment</option>
                    <option value="complaint">Complaint</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
                  <select value={formData.priority} onChange={(e) => setFormData({...formData, priority: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Message</label>
                <textarea required rows={4} value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg resize-none" />
              </div>
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setShowNewModal(false)} className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Send Message</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Announcement Modal */}
      {showAnnouncementModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">New Building Announcement</h3>
              <button onClick={() => setShowAnnouncementModal(false)} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <form onSubmit={handleAnnouncementSubmit} className="space-y-4">
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Property (Optional)</label>
                <select value={announcementData.entity_id} onChange={(e) => setAnnouncementData({...announcementData, entity_id: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg">
                  <option value="">All Properties</option>
                  {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                <input type="text" required value={announcementData.title} onChange={(e) => setAnnouncementData({...announcementData, title: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" placeholder="Announcement title" />
              </div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
                <select value={announcementData.priority} onChange={(e) => setAnnouncementData({...announcementData, priority: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg">
                  <option value="normal">Normal</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Content</label>
                <textarea required rows={5} value={announcementData.content} onChange={(e) => setAnnouncementData({...announcementData, content: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg resize-none" placeholder="Write your announcement here..." />
              </div>
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setShowAnnouncementModal(false)} className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Post Announcement</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
